<template>
  <section class="profile-page">
    <header class="profile-header">
      <div class="profile-avatar">
        {{ avatarText }}
      </div>
      <div class="profile-identity">
        <p>当前账号</p>
        <h1>{{ displayName }}</h1>
        <span>{{ emailText }}</span>
      </div>
    </header>

    <van-cell-group inset class="profile-card">
      <van-cell title="账号类型" :value="userRoleText" />
      <van-cell title="登录有效期" :value="expiresAtText" />
    </van-cell-group>

    <div class="profile-actions">
      <van-button block round type="danger" plain :loading="loggingOut" @click="handleLogout">
        退出登录
      </van-button>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { showToast } from 'vant'
import { useRouter } from 'vue-router'

import { logoutH5 } from '../api/auth'
import {
  clearH5Session,
  getH5ExpiresAt,
  getStoredH5User,
  onH5AuthChanged,
} from '../utils/auth'

const router = useRouter()
const user = ref(getStoredH5User())
const expiresAt = ref(getH5ExpiresAt())
const loggingOut = ref(false)

const stopListening = onH5AuthChanged(() => {
  user.value = getStoredH5User()
  expiresAt.value = getH5ExpiresAt()
})

onMounted(() => {
  user.value = getStoredH5User()
  expiresAt.value = getH5ExpiresAt()
})

onUnmounted(() => {
  stopListening()
})

const displayName = computed(() => (
  String(user.value?.displayName || user.value?.display_name || user.value?.email || '未命名账号').trim()
))

const emailText = computed(() => String(user.value?.email || '-').trim() || '-')

const avatarText = computed(() => {
  const source = displayName.value || emailText.value
  return String(source).trim().slice(0, 1).toUpperCase() || 'B'
})

const userRoleText = computed(() => (
  user.value?.isAdmin === true || Number(user.value?.isAdmin ?? user.value?.is_admin ?? 0) === 1
    ? '管理员'
    : '普通用户'
))

const expiresAtText = computed(() => {
  const rawValue = String(expiresAt.value || '').trim()
  if (!rawValue) return '-'
  const date = new Date(rawValue)
  if (Number.isNaN(date.getTime())) return rawValue
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
})

async function handleLogout() {
  loggingOut.value = true
  try {
    await logoutH5({
      skipAuthRedirect: true,
    }).catch(() => null)
    clearH5Session()
    showToast('已退出登录')
    await router.replace('/login')
  } finally {
    loggingOut.value = false
  }
}
</script>

<style scoped>
.profile-page {
  min-height: calc(100vh - 50px);
  padding: calc(env(safe-area-inset-top) + 18px) 16px calc(env(safe-area-inset-bottom) + 24px);
  box-sizing: border-box;
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
  padding: 20px 18px;
  border: 1px solid var(--yt-line);
  border-radius: var(--yt-radius-xl);
  background: var(--yt-surface-strong);
  box-shadow: var(--yt-shadow);
}

.profile-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 56px;
  height: 56px;
  border-radius: 18px;
  background: linear-gradient(135deg, #e11d48, #fb7185);
  color: #fff;
  font-size: 24px;
  font-weight: 800;
}

.profile-identity {
  min-width: 0;
}

.profile-identity p {
  margin: 0 0 6px;
  color: var(--yt-muted);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.profile-identity h1 {
  margin: 0;
  overflow: hidden;
  color: var(--yt-text);
  font-size: 22px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-identity span {
  display: block;
  margin-top: 6px;
  overflow: hidden;
  color: var(--yt-muted);
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-card {
  margin: 0;
  overflow: hidden;
  border: 1px solid var(--yt-line);
  box-shadow: var(--yt-shadow);
}

.profile-actions {
  margin-top: 18px;
}
</style>
